import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { SavedLocation } from '@hero/schema';
import { locationAutocompleteAPI } from '@hero/api';
import { convertToLatLng } from '@hero/core';
import { MapLocationData } from '@hero/web-components';
import { useLocationAutocomplete, useSavedLocationsList } from '@hero/web-hooks';
import { useAddressApi } from '@hero/api-hooks';
import type { MapSearchProps } from '@hero/web-components';

type SearchLocationData = locationAutocompleteAPI['data']['googlePlaces'][0];

export interface UseMapSearchReturn {
  // State
  inputState: {
    searchTerm: string;
    placeholder: string;
    isFocused: boolean;
    isTyping: boolean;
  };
  selectionState: {
    isFromMapClick: boolean;
    isFromSelection: boolean;
    isSelectedLocationSaved: boolean;
  };
  uiState: {
    showResults: boolean;
    activeTab: 'myPlaces' | 'client';
  };
  currentLocation: MapLocationData | null;
  isLoading: boolean;
  isLoadingCoordinates: boolean;
  isGettingCurrentLocation: boolean;

  // Data
  googlePlaces: SearchLocationData[];
  savedLocations: SavedLocation[];
  categorizedSavedLocations: {
    myPlaces: SavedLocation[];
    clientPlaces: SavedLocation[];
  };

  // Computed
  shouldShowTabs: boolean;
  shouldShowSearchResults: boolean;
  hasSearchTerm: boolean;

  // Handlers
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputFocus: () => void;
  handleInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => Promise<void>;
  handleSelectGooglePlace: (place: SearchLocationData) => void;
  handleSelectSavedLocation: (savedLocation: SavedLocation) => void;
  handleCoordinates: (lat: number, lng: number, index: number) => Promise<void>;
  handleGetCurrentLocation: () => Promise<void>;
  handleClear: () => void;

  // Refs
  searchInputRef: React.RefObject<HTMLInputElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  isClosingDropdownRef: React.MutableRefObject<boolean>;

  // Tab handlers
  setActiveTab: (tab: 'myPlaces' | 'client') => void;
  onLoadMoreMyPlaces: () => void;
  onLoadMoreClientPlaces: () => void;
  hasNextMyPlaces: boolean;
  hasNextClientPlaces: boolean;
  isFetchingNextMyPlaces: boolean;
  isFetchingNextClientPlaces: boolean;
  isLoadingMyPlaces: boolean;
  isLoadingClientPlaces: boolean;
}

export function useMapSearch(props: MapSearchProps): UseMapSearchReturn {
  const {
    map,
    onLocationSelect,
    showSearch,
    searchMode = 'google-and-saved-locations',
    client,
    minSearchLength = 2,
    isEditMode = false,
    externalSearchTerm,
    editingIndex,
    onCurrentLocationChange,
    isClosingDropdownRef: externalIsClosingDropdownRef,
  } = props;

  const addressApi = useAddressApi();

  // Consolidated state
  const [searchTerm, setSearchTerm] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [isFromMapClick, setIsFromMapClick] = useState(false);
  const [isFromSelection, setIsFromSelection] = useState(false);
  const [isSelectedLocationSaved, setIsSelectedLocationSaved] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState<'myPlaces' | 'client'>('myPlaces');
  const [currentLocation, setCurrentLocation] = useState<MapLocationData | null>(null);
  const [isGettingCurrentLocation, setIsGettingCurrentLocation] = useState(false);
  const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null!);
  const searchInputRef = useRef<HTMLInputElement>(null!);
  const internalIsClosingDropdownRef = useRef(false);
  const closingDropdownRef = externalIsClosingDropdownRef || internalIsClosingDropdownRef;

  // Extract client config — `thirdPartyCustomerId` is the existing
  // Order API field name (untouched in this PR); locally we rebind it
  // to `clientId` to match the saved-location API surface.
  const orgId = client?.orgId?.toString();
  const clientId = client?.thirdPartyCustomerId?.toString();
  const fetchSavedPlaces = searchMode === 'google-and-saved-locations';
  const hasSearchTerm = searchTerm.length >= minSearchLength;

  // Use autocomplete only when there's a search term AND it's not from a map click
  const shouldFetchAutocomplete = showSearch && hasSearchTerm && !isFromMapClick;

  // Location autocomplete hook (uses useAutocompleteApi internally)
  const {
    googlePlaces,
    savedLocations,
    isLoading: isLoadingAutocomplete,
  } = useLocationAutocomplete(searchTerm, {
    minSearchLength,
    enabled: shouldFetchAutocomplete,
    orgId,
    clientId,
  });

  // Saved locations hooks for tabs (use useSavedLocationListApi internally)
  const {
    data: myPlacesData,
    isLoading: isLoadingMyPlaces,
    onLoadMore: onLoadMoreMyPlaces,
    hasNextPage: hasNextMyPlaces,
    isFetchingNextPage: isFetchingNextMyPlaces,
  } = useSavedLocationsList({
    enabled:
      showSearch && fetchSavedPlaces && showResults && !hasSearchTerm && activeTab === 'myPlaces',
    limit: 5,
    isOwnBranch: true,
  });

  const {
    data: clientPlacesData,
    isLoading: isLoadingClientPlaces,
    onLoadMore: onLoadMoreClientPlaces,
    hasNextPage: hasNextClientPlaces,
    isFetchingNextPage: isFetchingNextClientPlaces,
  } = useSavedLocationsList({
    enabled:
      showSearch && fetchSavedPlaces && showResults && !hasSearchTerm && activeTab === 'client',
    limit: 5,
    isOwnBranch: false,
    clientId: clientId
      ? typeof clientId === 'string'
        ? clientId
        : String(clientId)
      : undefined,
  });

  // Categorize saved locations
  const categorizedSavedLocations = useMemo(() => {
    return {
      myPlaces: myPlacesData || [],
      clientPlaces: clientPlacesData || [],
    };
  }, [myPlacesData, clientPlacesData]);

  const isLoading = isLoadingAutocomplete || isLoadingMyPlaces || isLoadingClientPlaces;

  // External search term syncing (for map location updates in single edit mode)
  useEffect(() => {
    if (
      externalSearchTerm !== undefined &&
      isEditMode &&
      !isUserTyping &&
      !isInputFocused &&
      !isFromSelection &&
      externalSearchTerm !== placeholder &&
      externalSearchTerm !== searchTerm
    ) {
      setPlaceholder(externalSearchTerm);
      setSearchTerm('');
      setIsFromMapClick(true);
      setIsFromSelection(false);
    }
  }, [
    externalSearchTerm,
    isEditMode,
    placeholder,
    searchTerm,
    isUserTyping,
    isInputFocused,
    isFromSelection,
  ]);

  // Handle coordinates - fetch address and update location (uses useAddressApi internally)
  const handleCoordinates = useCallback(
    async (lat: number, lng: number, index: number) => {
      if (!onLocationSelect) return;

      setIsLoadingCoordinates(true);

      try {
        const address = await addressApi.mutateAsync({ lat, lng });

        const location: MapLocationData = {
          cords: { lat, lng },
          fullAddress: address?.fullAddress || `${lat}, ${lng}`,
          city: address?.city?.name,
          address: address || undefined,
          isAccurateLocation: false,
          isLoadingAddress: false,
        };

        setPlaceholder(location.fullAddress || `${lat}, ${lng}`);
        setSearchTerm('');
        setIsUserTyping(false);
        setIsFromMapClick(true);
        setIsFromSelection(false);

        onLocationSelect(location, index);

        if (map) {
          map.panTo({ lat, lng });
        }
      } catch (error) {
        console.error('Failed to fetch address from coordinates:', error);
        const location: MapLocationData = {
          cords: { lat, lng },
          fullAddress: `${lat}, ${lng}`,
          isAccurateLocation: false,
          isLoadingAddress: false,
        };

        setPlaceholder(`${lat}, ${lng}`);
        setSearchTerm('');
        setIsFromMapClick(true);
        setIsFromSelection(false);
        onLocationSelect(location, index);

        if (map) {
          map.panTo({ lat, lng });
        }
      } finally {
        setIsLoadingCoordinates(false);
      }
    },
    [onLocationSelect, addressApi.mutateAsync, map],
  );

  // Parse coordinates from input string
  const parseCoordinates = (input: string): { lat: number; lng: number } | null => {
    const trimmed = input.trim();
    const patterns = [
      /^\(?\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*\)?$/,
      /^lat\s*:?\s*(-?\d+\.?\d*)\s*,?\s*lng\s*:?\s*(-?\d+\.?\d*)$/i,
      /^lng\s*:?\s*(-?\d+\.?\d*)\s*,?\s*lat\s*:?\s*(-?\d+\.?\d*)$/i,
    ];

    for (const pattern of patterns) {
      const match = trimmed.match(pattern);
      if (match) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          return { lat, lng };
        }
      }
    }
    return null;
  };

  // Handle search by coordinates
  const handleSearchByCoordinates = async (lat: number, lng: number) => {
    if (!map || !onLocationSelect) return;
    setShowResults(false);
    const targetIndex =
      isEditMode && editingIndex !== null && editingIndex !== undefined ? editingIndex : 0;
    await handleCoordinates(lat, lng, targetIndex);
    if (map) {
      map.setZoom(15);
    }
  };

  // Input handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsUserTyping(true);
    setIsFromMapClick(false);
    setIsFromSelection(false);
    setIsSelectedLocationSaved(false);
    setShowResults(term.length >= minSearchLength || fetchSavedPlaces);
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);

    if (isSelectedLocationSaved && !isUserTyping) {
      if (fetchSavedPlaces) {
        setShowResults(true);
      }
    } else if (searchTerm.length >= minSearchLength) {
      setShowResults(true);
    } else if (fetchSavedPlaces) {
      setShowResults(true);
    } else {
      setShowResults(searchTerm.length >= minSearchLength);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      const activeElement = document.activeElement;
      const isClickingInResults = containerRef.current?.contains(activeElement);

      if (!isClickingInResults) {
        setIsInputFocused(false);
        setIsUserTyping(false);
      }
    }, 150);
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const coordinates = parseCoordinates(searchTerm);
      if (coordinates) {
        e.preventDefault();
        await handleSearchByCoordinates(coordinates.lat, coordinates.lng);
      }
    }
  };

  // Location selection handlers
  const handleSelectGooglePlace = (place: SearchLocationData) => {
    if (!place.location || !onLocationSelect) return;

    const location: MapLocationData = {
      cords: place.location,
      fullAddress: place.fullAddress,
      isAccurateLocation: false,
    };

    const targetIndex =
      isEditMode && editingIndex !== null && editingIndex !== undefined ? editingIndex : 0;
    onLocationSelect(location, targetIndex);
    setSearchTerm(place.fullAddress || '');
    setPlaceholder('');
    setIsUserTyping(false);
    setIsFromMapClick(false);
    setIsFromSelection(true);
    setIsSelectedLocationSaved(false);
    setShowResults(false);
    setIsInputFocused(false);
    searchInputRef.current?.blur();

    if (map) {
      map.setCenter(location.cords);
      map.setZoom(15);
    }
  };

  const handleSelectSavedLocation = (savedLocation: SavedLocation) => {
    if (!savedLocation.address?.location || !onLocationSelect) return;

    try {
      const converted = convertToLatLng(savedLocation.address.location);
      const cords = { lat: converted.lat, lng: converted.lng };

      const location: MapLocationData = {
        cords,
        city: savedLocation.address.city?.name,
        fullAddress: savedLocation.address.fullAddress,
        address: savedLocation.address,
        savedLocation,
        savedLocationId: savedLocation._id,
        isAccurateLocation: true,
      };

      const targetIndex =
        isEditMode && editingIndex !== null && editingIndex !== undefined ? editingIndex : 0;
      onLocationSelect(location, targetIndex);

      const displayName = savedLocation.name || savedLocation.address?.fullAddress || '';
      setSearchTerm(displayName);
      setPlaceholder('');
      setIsUserTyping(false);
      setIsFromMapClick(false);
      setIsFromSelection(true);
      setIsSelectedLocationSaved(true);
      setShowResults(false);
      setIsInputFocused(false);
      searchInputRef.current?.blur();

      if (map) {
        map.setCenter(cords);
        map.setZoom(15);
      }
    } catch (error) {
      console.error('Error in handleSelectSavedLocation:', error);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setPlaceholder('');
    setIsFromMapClick(false);
    setIsFromSelection(false);
    setIsSelectedLocationSaved(false);
    setShowResults(false);
    searchInputRef.current?.focus();
  };

  // Get current location (uses useAddressApi internally)
  const handleGetCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation || !onLocationSelect) {
      return;
    }

    setIsGettingCurrentLocation(true);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve,
          () => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 300000,
            });
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
        );
      });

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const address = await addressApi.mutateAsync({ lat, lng });

      const location: MapLocationData = {
        cords: { lat, lng },
        fullAddress: address?.fullAddress || `${lat}, ${lng}`,
        city: address?.city?.name,
        address: address || undefined,
        isAccurateLocation: true,
        isLoadingAddress: false,
      };

      setCurrentLocation(location);

      if (onCurrentLocationChange) {
        onCurrentLocationChange(location);
      }

      const targetIndex =
        isEditMode && editingIndex !== null && editingIndex !== undefined ? editingIndex : 0;
      onLocationSelect(location, targetIndex);

      setPlaceholder(location.fullAddress || `${lat}, ${lng}`);
      setSearchTerm('');
      setIsFromMapClick(true);
      setIsFromSelection(false);
      setShowResults(false);

      if (map) {
        map.panTo({ lat, lng });
        map.setZoom(15);
      }
    } catch (error) {
      console.error('Failed to get current location:', error);
    } finally {
      setIsGettingCurrentLocation(false);
    }
  }, [
    onLocationSelect,
    addressApi.mutateAsync,
    map,
    isEditMode,
    editingIndex,
    onCurrentLocationChange,
  ]);

  // Close results when clicking outside
  useEffect(() => {
    if (!showResults) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInside = containerRef.current?.contains(target);

      if (containerRef.current && !isClickInside) {
        const refToUse = externalIsClosingDropdownRef || internalIsClosingDropdownRef;
        refToUse.current = true;

        setTimeout(() => {
          refToUse.current = false;
        }, 200);

        setShowResults(false);
        setIsInputFocused(false);
        searchInputRef.current?.blur();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [showResults, externalIsClosingDropdownRef, internalIsClosingDropdownRef]);

  // Computed values
  const shouldShowTabs =
    showResults &&
    searchMode === 'google-and-saved-locations' &&
    fetchSavedPlaces &&
    (!hasSearchTerm || (isSelectedLocationSaved && !isUserTyping));

  const shouldShowSearchResults =
    showResults && hasSearchTerm && (isUserTyping || !isSelectedLocationSaved);

  return {
    // State
    inputState: {
      searchTerm,
      placeholder,
      isFocused: isInputFocused,
      isTyping: isUserTyping,
    },
    selectionState: {
      isFromMapClick,
      isFromSelection,
      isSelectedLocationSaved,
    },
    uiState: {
      showResults,
      activeTab,
    },
    currentLocation,
    isLoading,
    isLoadingCoordinates,
    isGettingCurrentLocation,

    // Data
    googlePlaces,
    savedLocations,
    categorizedSavedLocations,

    // Computed
    shouldShowTabs,
    shouldShowSearchResults,
    hasSearchTerm,

    // Handlers
    handleSearchChange,
    handleInputFocus,
    handleInputBlur,
    handleKeyDown,
    handleSelectGooglePlace,
    handleSelectSavedLocation,
    handleCoordinates,
    handleGetCurrentLocation,
    handleClear,

    // Refs
    searchInputRef,
    containerRef,
    isClosingDropdownRef: closingDropdownRef,

    // Tab handlers
    setActiveTab,
    onLoadMoreMyPlaces,
    onLoadMoreClientPlaces,
    hasNextMyPlaces,
    hasNextClientPlaces,
    isFetchingNextMyPlaces,
    isFetchingNextClientPlaces,
    isLoadingMyPlaces,
    isLoadingClientPlaces,
  };
}
